import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';

// Types
import { Props, VehicleManagement } from './types';
import {
  VehicleOwnership,
  VehicleUsage,
  VehicleOwnershipOptions,
} from '../../../types/vehicles';

// Schema
import {
  VehicleManagementSchema,
  VehicleManagementSchemaConditional,
  VehicleManagementSchemaNoConcession,
} from './schema';

// Styles
import {
  Container,
  InputsContainer,
  AddVehicleButton,
} from './styles';

// Components
import InputText from '../../inputs/InputText';
import MultiSelectDropdown from '../../inputs/DropDown/MultiSelectDropdown';

// Utils
import { useCacheFormInSessionStorage } from '../../../hooks/useCacheForm';
import { Typography } from '../../display/Typography';
import { ImperativeRefInput } from '../../inputs/DropDown/DropDownInput/types';
import { ImperativeRefMultiSelect } from '../../inputs/DropDown/MultiSelectDropdown/types';
import DropDownInput from '../../inputs/DropDown/DropDownInput';

export const AddVehicleManagementForm = ({
  onFormSubmit,
  namingPersistForm,
  initialData,
  showOwnershipUsageSelectors = false,
  showOnlyOwnershipSelector = false,
  requireConcessionMode = 'always',
  hideButton = false,
  hideTitle = false,
}: Props) => {
  const { t } = useTranslation(['forms', 'management']);

  // Select schema based on requireConcessionMode
  const validationSchema = useMemo(() => {
    if (requireConcessionMode === 'internal') {
      return VehicleManagementSchemaConditional;
    } else if (requireConcessionMode === 'never') {
      return VehicleManagementSchemaNoConcession;
    }
    return VehicleManagementSchema; // 'always' by default
  }, [requireConcessionMode]);

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
    reset,
    setValue,
  } = useForm<VehicleManagement>({
    defaultValues: {
      ...initialData.cacheData,
      isActive: true,
      // ✅ Defaults con ownership y usage
      ownership: VehicleOwnership.INTERNAL,
      usage: VehicleUsage.CONSTRUCTION, // ✅ Por defecto CONSTRUCTION
      teamIDs: [], // ✅ M2M: Array de equipos por defecto vacío
    },
    resolver: yupResolver(validationSchema) as any,
  });
  useCacheFormInSessionStorage(namingPersistForm, control);

  // States
  const [repeatedPlateError, setRepeatedPlateError] = useState<null | string>(
    null
  );

  const vehicleCategoryRef = useRef<ImperativeRefInput>(null);
  const vehicleConcessionRef = useRef<ImperativeRefInput>(null);
  const vehicleOwnershipRef = useRef<ImperativeRefInput>(null);
  const teamsRef = useRef<ImperativeRefMultiSelect>(null);

  const concessionOptions = useMemo(
    () =>
      initialData.concessions.map((concession) => ({
        label: concession.name,
        value: concession.id,
      })),
    [initialData.concessions]
  );

  // Auto-asignar concesión cuando solo hay 1 (reemplaza side-effect en render)
  useEffect(() => {
    if (concessionOptions.length === 1) {
      setValue('concession', concessionOptions[0].value);
    }
  }, [concessionOptions, setValue]);

  const onSubmit = async (data: VehicleManagement) => {
    const errorMessage = await onFormSubmit(data);
    if (errorMessage) {
      setRepeatedPlateError(errorMessage);
    } else {
      setRepeatedPlateError(null);
      reset({
        plate: '',
        model: '',
        category: '',
        teamIDs: [],
      });
      vehicleCategoryRef.current?.clearSelected();
      vehicleConcessionRef.current?.clearSelected();
      vehicleOwnershipRef.current?.clearSelected();
      teamsRef.current?.clearSelected();

      // 🧹 Limpiar cache del formulario después de añadir exitosamente
      // Este es un formulario temporal para añadir NUEVOS vehículos, no tiene sentido persistir
      if (namingPersistForm) {
        console.log(
          '🧹 [AddVehicleManagementForm] Limpiando cache:',
          namingPersistForm
        );
        sessionStorage.removeItem(namingPersistForm);
      }
    }
  };

  // Handle button click
  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent parent form submission
    handleSubmit(onSubmit)();
  };

  return (
    <Container>
      {!hideTitle && (
        <Typography size="L" variant="semiBold">
          {t('management.addVehicle')}
        </Typography>
      )}

      <InputsContainer>
        <InputText
          {...register('plate')}
          label={t('vehicle.plate')}
          typeInput={'normal'}
          inputSize="S"
          placeholder={t('vehicle.plate')}
          borderError={!!errors.plate?.message}
        />
        <InputText
          {...register('model')}
          label={t('vehicle.model')}
          typeInput={'normal'}
          placeholder={t('vehicle.model')}
          borderError={!!errors.model?.message}
        />
        <Controller
          control={control}
          name="category"
          render={({ field: { onChange }, formState: { defaultValues } }) => {
            const options =
              initialData?.vehicleCategories?.map((category) => ({
                label: category?.name || '',
                value: category?.id || '',
              })) || [];

            return (
              <DropDownInput
                ref={vehicleCategoryRef}
                inputSize="M"
                onChangeSelected={(option) => onChange(option?.value ?? '')}
                typeDropDown="Default"
                labelText={t('vehicle.category')}
                placeholder={t('common:labels.select')}
                defaultValue={defaultValues?.category}
                borderError={!!errors.category?.message}
                options={options}
              />
            );
          }}
        />

        {/* ✅ Selector de ownership - ANTES de concesión */}
        {(showOwnershipUsageSelectors || showOnlyOwnershipSelector) && (
            <Controller
              control={control}
              name="ownership"
            render={({ field: { onChange }, formState: { defaultValues } }) => {
                return (
                  <DropDownInput
                    ref={vehicleOwnershipRef}
                    inputSize="M"
                    onChangeSelected={(option) =>
                      onChange(option?.value as VehicleOwnership)
                    }
                    typeDropDown="Default"
                    labelText={t('vehicle.ownership', 'Propiedad')}
                    placeholder={t('common:labels.select', 'Seleccionar')}
                    defaultValue={defaultValues?.ownership}
                    borderError={!!errors.ownership?.message}
                    options={VehicleOwnershipOptions}
                  />
                );
              }}
            />
        )}

        <Controller
          control={control}
          name="concession"
          render={({
            field: { onChange, value },
            formState: { defaultValues },
          }) => {
            // Si solo hay una concesión, no mostrar selector (useEffect ya asignó el valor)
            if (concessionOptions.length === 1) {
              return <></>;
            }

            // Si hay múltiples concesiones, mostrar dropdown
            return (
              <DropDownInput
                ref={vehicleConcessionRef}
                inputSize="XS"
                onChangeSelected={(option) => onChange(option?.value ?? '')}
                typeDropDown="Default"
                labelText={t('user.concession')}
                placeholder={t('common:labels.select')}
                defaultValue={defaultValues?.concession}
                borderError={!!errors.concession?.message}
                options={concessionOptions}
              />
            );
          }}
        />

        {/* Selector de equipos (multi-select con checkboxes) */}
        {initialData.teams && initialData.teams.length > 0 && (
          <Controller
            control={control}
            name="teamIDs"
            render={({ field: { onChange, value } }) => (
              <MultiSelectDropdown
                ref={teamsRef}
                inputSize="M"
                typeDropDown="Default"
                labelText={t('management:teams.assignedTeams', 'Equipos asignados')}
                placeholder={t('management:teams.selectTeams', 'Seleccionar equipos')}
                options={initialData.teams.map((team) => ({
                  label: team.name,
                  value: team.id,
                }))}
                selectedValues={value || []}
                onChangeSelected={onChange}
              />
            )}
          />
        )}

        {!hideButton && (
          <AddVehicleButton type="button" onClick={handleButtonClick}>
            ⮕
          </AddVehicleButton>
        )}
        {repeatedPlateError && !errors.plate?.message && (
          <Typography colorText="lightCoralRed" size="XS">
            {repeatedPlateError}
          </Typography>
        )}
      </InputsContainer>
    </Container>
  );
};
